import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SafeImage } from "@/components/SafeImage";
import { CalendarDays, ArrowUpRight, Ticket } from "lucide-react";
import ReservationDialog from "@/components/site/ReservationDialog";
import TicketDialog from "@/components/site/TicketDialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function dateParts(iso) {
  if (!iso) return { mo: "", day: "", dow: "" };
  try {
    const d = new Date(iso + "T00:00:00");
    return {
      mo: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
      day: d.getDate(),
      dow: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    };
  } catch {
    return { mo: "", day: "", dow: "" };
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState(null);
  const [ticketEvent, setTicketEvent] = useState(null);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) {
    return (
      <section
        id="events"
        data-testid="events-section"
        className="trio-section py-24 lg:py-32 bg-[var(--trio-text)] text-[var(--trio-bg)]"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-4">
            On the Calendar
          </div>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mb-6">
            Events <span className="text-[var(--trio-primary)]">coming soon.</span>
          </h2>
          <p className="text-base lg:text-lg text-[var(--trio-bg)]/70 leading-relaxed max-w-xl mx-auto mb-8">
            Something's cooking. Join the list and we'll ring your line the moment
            the next Trio night drops. In the meantime, the weekly vibes are always on.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="/specials"
              data-testid="events-empty-specials"
              className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-3 text-xs tracking-widest uppercase font-bold transition-colors"
            >
              See This Week's Specials
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="/reservations"
              data-testid="events-empty-reserve"
              className="inline-flex items-center gap-2 border border-[var(--trio-bg)]/40 hover:border-[var(--trio-accent)] hover:text-[var(--trio-accent)] text-[var(--trio-bg)] px-5 py-3 text-xs tracking-widest uppercase font-bold transition-colors"
            >
              Reserve a Table
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    );
  }

  const featured = events.filter((e) => e.featured);
  const calendar = events.filter((e) => !e.featured);

  return (
    <section
      id="events"
      data-testid="events-section"
      className="trio-section py-24 lg:py-32 bg-[var(--trio-text)] text-[var(--trio-bg)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-4 flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" /> What&apos;s Happening
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9]">
              On the<br />
              <span className="text-[var(--trio-accent)]">Calendar.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <p className="text-base lg:text-lg text-[var(--trio-bg)]/70 leading-relaxed">
              The big nights below — and the regular run of things further
              down. Tap a featured card to lock your spot.
            </p>
          </div>
        </div>

        {/* Featured events — big cards */}
        {featured.length > 0 && (
          <div
            data-testid="events-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {featured.slice(0, 6).map((ev, i) => (
              <article
                key={ev.id}
                data-testid={`event-card-${i}`}
                className="group bg-[var(--trio-bg)] text-[var(--trio-text)] border border-[var(--trio-bg)]/10 hover:border-[var(--trio-accent)] transition-colors overflow-hidden flex flex-col"
              >
                {ev.image_url && (
                  <div className="relative aspect-square overflow-hidden">
                    <SafeImage
                      src={ev.image_url}
                      alt={ev.title}
                      testid={`event-card-img-${i}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-3">
                    {formatDate(ev.event_date)}
                    {ev.event_time ? ` · ${ev.event_time}` : ""}
                  </div>
                  <h3 className="font-display uppercase text-2xl tracking-tight leading-tight mb-2">
                    {ev.title}
                  </h3>
                  <p className="text-sm text-[var(--trio-muted)] leading-relaxed flex-1">
                    {ev.description}
                  </p>
                  {ev.has_tickets && ev.ticket_price ? (
                    <div className="mt-5 flex gap-2">
                      <button
                        onClick={() => setTicketEvent(ev)}
                        data-testid={`event-buy-tickets-${i}`}
                        className="flex-1 group/btn inline-flex items-center justify-between gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-3 text-[11px] tracking-widest uppercase font-bold transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <Ticket className="w-3.5 h-3.5" />
                          Buy ${Number(ev.ticket_price).toFixed(0)}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                      </button>
                      <button
                        onClick={() => setActiveEvent(ev)}
                        data-testid={`event-rsvp-btn-${i}`}
                        className="inline-flex items-center gap-2 border border-[var(--trio-text)]/25 hover:border-[var(--trio-primary)] px-4 py-3 text-[11px] tracking-widest uppercase font-bold"
                        title="Reserve without buying tickets"
                      >
                        RSVP
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setActiveEvent(ev)}
                      data-testid={`event-rsvp-btn-${i}`}
                      className="mt-5 group/btn inline-flex items-center justify-between gap-3 bg-[var(--trio-text)] hover:bg-[var(--trio-primary)] text-[var(--trio-bg)] px-5 py-3 text-[11px] tracking-widest uppercase font-bold transition-colors"
                    >
                      Reserve for this event
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Non-featured — calendar list */}
        {calendar.length > 0 && (
          <div
            data-testid="events-calendar-list"
            className={`${featured.length > 0 ? "mt-16 pt-10 border-t border-[var(--trio-bg)]/15" : ""}`}
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-bg)]/60 font-bold mb-6">
              Also on the calendar
            </div>
            <ul className="divide-y divide-[var(--trio-bg)]/10">
              {calendar.map((ev, i) => {
                const dp = dateParts(ev.event_date);
                return (
                  <li
                    key={ev.id}
                    data-testid={`calendar-row-${i}`}
                    className="py-5 grid grid-cols-12 gap-4 items-center"
                  >
                    {/* date block */}
                    <div className="col-span-3 sm:col-span-2 flex items-baseline gap-3">
                      <div className="font-display text-3xl sm:text-4xl tracking-tight leading-none text-[var(--trio-accent)]">
                        {dp.day}
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-bg)]/60 font-bold leading-none">
                          {dp.mo}
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--trio-bg)]/40 mt-1">
                          {dp.dow}
                        </div>
                      </div>
                    </div>

                    {/* title + description */}
                    <div className="col-span-9 sm:col-span-7">
                      <div className="font-display uppercase text-xl tracking-tight leading-tight">
                        {ev.title}
                        {ev.has_tickets && ev.ticket_price ? (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] tracking-widest bg-[var(--trio-accent)] text-[var(--trio-text)] px-2 py-0.5 align-middle">
                            <Ticket className="w-3 h-3" /> ${Number(ev.ticket_price).toFixed(0)}
                          </span>
                        ) : null}
                      </div>
                      {ev.description && (
                        <p className="text-sm text-[var(--trio-bg)]/65 line-clamp-1 mt-0.5">
                          {ev.description}
                        </p>
                      )}
                    </div>

                    {/* time / cta */}
                    <div className="hidden sm:flex col-span-3 items-center justify-end gap-2">
                      {ev.has_tickets && ev.ticket_price ? (
                        <button
                          onClick={() => setTicketEvent(ev)}
                          data-testid={`calendar-buy-tickets-${i}`}
                          className="text-[10px] tracking-widest uppercase font-bold bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-3 py-2"
                        >
                          Buy Tickets
                        </button>
                      ) : (
                        <span className="text-xs uppercase tracking-widest text-[var(--trio-bg)]/60">
                          {ev.event_time || "All night"}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <ReservationDialog
        open={!!activeEvent}
        onOpenChange={(o) => !o && setActiveEvent(null)}
        mode="event"
        event={activeEvent}
      />
      <TicketDialog
        open={!!ticketEvent}
        onOpenChange={(o) => !o && setTicketEvent(null)}
        event={ticketEvent}
      />
    </section>
  );
}
